import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Box, Camera, Play } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getIcon = (iconName) => {
  const icons = { Box, Camera, Play };
  return icons[iconName] || Box;
};

// ServicesTab — admin CRUD for service offerings priced in tokens
const ServicesTab = () => {
  const [services, setServices] = useState([]);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: '', description: '', icon: 'Box', price_tokens: 100, features: [], is_active: true, order: 0
  });
  const [featureInput, setFeatureInput] = useState('');

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/services`, { withCredentials: true });
      setServices(r.data);
    } catch {
      toast.error('Erreur de chargement');
    }
  };
  useEffect(() => { load(); }, []);

  const resetServiceForm = () => {
    setServiceForm({ name: '', description: '', icon: 'Box', price_tokens: 100, features: [], is_active: true, order: 0 });
    setEditingService(null);
    setFeatureInput('');
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setServiceForm({ ...serviceForm, features: [...serviceForm.features, featureInput.trim()] });
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setServiceForm({ ...serviceForm, features: serviceForm.features.filter((_, i) => i !== index) });
  };

  const handleCreateService = async () => {
    try {
      if (editingService) {
        await axios.put(`${API}/admin/services/${editingService.service_id}`, serviceForm, { withCredentials: true });
        toast.success('Service mis à jour');
      } else {
        await axios.post(`${API}/admin/services`, serviceForm, { withCredentials: true });
        toast.success('Service créé');
      }
      load();
      setShowServiceDialog(false);
      resetServiceForm();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleDeleteService = async (serviceId) => {
    try {
      await axios.delete(`${API}/admin/services/${serviceId}`, { withCredentials: true });
      setServices(services.filter(s => s.service_id !== serviceId));
      toast.success('Service supprimé');
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium">Services / Offres</h2>
        <Button onClick={() => { resetServiceForm(); setShowServiceDialog(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Nouveau service
        </Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map(service => {
          const Icon = getIcon(service.icon);
          return (
            <div key={service.service_id} className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Icon className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => {
                    setEditingService(service);
                    setServiceForm({ name: service.name, description: service.description, icon: service.icon, price_tokens: service.price_tokens, features: service.features || [], is_active: service.is_active, order: service.order });
                    setShowServiceDialog(true);
                  }}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteService(service.service_id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <h3 className="font-medium text-gray-900">{service.name}</h3>
              <p className="text-sm text-gray-500 mt-1">{service.description}</p>
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                <span className="text-lg font-semibold">{service.price_tokens} jetons</span>
                <span className={`text-xs px-2 py-1 rounded ${service.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {service.is_active ? 'Actif' : 'Inactif'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingService ? 'Modifier le service' : 'Nouveau service'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Nom *</label>
              <Input value={serviceForm.name} onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })} placeholder="Ex: Génération Shoebox" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Description</label>
              <Input value={serviceForm.description} onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })} placeholder="Description du service" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Icône</label>
                <select value={serviceForm.icon} onChange={(e) => setServiceForm({ ...serviceForm, icon: e.target.value })} className="w-full border border-gray-300 rounded-lg p-2">
                  <option value="Box">Box</option>
                  <option value="Camera">Camera</option>
                  <option value="Play">Play</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Prix (jetons)</label>
                <Input type="number" value={serviceForm.price_tokens} onChange={(e) => setServiceForm({ ...serviceForm, price_tokens: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Fonctionnalités</label>
              <div className="flex gap-2 mb-2">
                <Input value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} placeholder="Ajouter une fonctionnalité" onKeyDown={(e) => e.key === 'Enter' && addFeature()} />
                <Button type="button" onClick={addFeature}>+</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {serviceForm.features.map((f, i) => (
                  <span key={i} className="bg-gray-100 px-2 py-1 rounded text-sm flex items-center gap-1">
                    {f}
                    <button onClick={() => removeFeature(i)} className="text-gray-400 hover:text-red-500">x</button>
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={serviceForm.is_active} onChange={(e) => setServiceForm({ ...serviceForm, is_active: e.target.checked })} className="rounded" />
              <label className="text-sm text-gray-700">Service actif</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowServiceDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateService} className="bg-blue-600 hover:bg-blue-700">{editingService ? 'Mettre à jour' : 'Créer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ServicesTab;
